/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  BandWidthOption,
  ConnectionEvent,
  ConnectionState,
  ErrorCode,
  LocalAudioTrack,
  LocalScreenTrack,
  LocalVideoTrack,
  NAME,
  NOT_SUPPORTED_H264,
  RtcError,
  SDP_SEMANTICS_PLAN_B,
  TRACK_ACTION,
  TrackEvent,
  TRANSPORT_DIRECTION,
  UPDATE_OFFER_TIMEOUT,
  ILocalTrack,
  VideoCodec,
  SignalResponse
} from 'trtc-js-sdk-core';

import Connection from './connection';

import {
  IS_GET_SETTINGS_SUPPORTED,
  isAddTransceiverSupported,
  isGetTransceiversSupported,
  isReplaceTrackSupported,
  isSetParametersSupported
} from 'trtc-js-sdk-core/src/common/rtc-detection';

import { SignalCommand, SignalEvent } from '../signal/constant';
import * as SdpParser from './sdp';
import { IS_FIREFOX, IS_IOS_15_1, IS_SAFARI_15_1 } from 'trtc-js-sdk-core/src/utils/environment';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import { ErrorInfo, ErrorName } from 'trtc-js-sdk-core/src/common/error-info';
import { getReconnectionTimeout, isFunction } from 'trtc-js-sdk-core/src/utils/utils';
import INNER_EVENT from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import { after, before, createDecorator } from 'trtc-js-sdk-core/src/utils/decorators/middleware';
import { IUplinkConnection } from '../type/type';
import { KV_REPORT_KEY_NETWORK } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import { reportSuccessRate } from 'trtc-js-sdk-core/src/utils/decorators/report-success-rate';
import { UplinkConnectionOption } from './type';
import { addCallQueue } from 'trtc-js-sdk-core/src/utils/decorators/api-call-queue';


// default sdp offer options
const offerOptions = {
  // offerToReceiveAudio: true,
  // offerToReceiveVideo: true,
  voiceActivityDetection: false
};

/**
 * 继承自 Connection 类，将上行相关功能逻辑从 Connection 中分离
 * 除了提供基础 Connection 功能之外，还提供上行重连功能
 * @class UplinkConnection
 * @extends {Connection}
 */
class UplinkConnection extends Connection implements IUplinkConnection {
  localMainAudioTrack?: LocalAudioTrack | null = null;
  localMainVideoTrack?: LocalVideoTrack | LocalScreenTrack | null = null;
  localAuxAudioTrack?: LocalAudioTrack | null = null;
  localAuxVideoTrack?: LocalVideoTrack | LocalScreenTrack | null = null;
  // 记录上行流的 ssrc，可在上报 monitor 时，根据 ssrc 来区分数据类型
  ssrc = { audio: 0, video: 0, small: 0, auxiliary: 0 };
  private _isPublishingAux = false;
  private _publishingLocalAudioTrack: LocalAudioTrack | null;
  private _publishingLocalVideoTrack: LocalVideoTrack | null;
  private _mediaSettings = {
    videoCodec: '',
    videoWidth: 0,
    videoHeight: 0,
    videoBps: 0, // bps
    videoFps: 0,

    audioCodec: 'opus',
    audioFs: 0, // 采样率
    audioChannel: 0, // 声道数
    audioBps: 0, // 单位bps

    smallVideoWidth: 0,
    smallVideoHeight: 0,
    smallVideoFps: 0,
    smallVideoBps: 0,

    auxVideoWidth: 0,
    auxVideoHeight: 0,
    auxVideoFps: 0,
    auxVideoBps: 0
  };

  flag = 0;
  constructor(options: UplinkConnectionOption) {
    super({ ...options, isUplink: true });
  }

  get videoCodec() {
    return (this._mediaSettings.videoCodec.toLowerCase() || 'h264') as VideoCodec;
  }

  get isMainStreamPublished() {
    return !!(this.localMainAudioTrack || this.localMainVideoTrack);
  }

  get isAuxStreamPublished() {
    return !!(this.localAuxVideoTrack || this.localAuxAudioTrack);
  }

  isTrackReferenced(localTrack: ILocalTrack): boolean {
    return localTrack === this.localMainAudioTrack || localTrack === this.localMainVideoTrack
      || localTrack === this.localAuxVideoTrack || localTrack === this.localAuxAudioTrack;
  }

  initialize() {
    super.initialize();

    this.installEvents();
  }

  reset() {
    if (this._isReconnecting) {
      this.stopReconnection();
    }

    this.closePeerConnection();
    this.uninstallEvents();
  }

  close(reason?: string) {
    super.close(reason);
    this.reset();
    this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
  }

  /**
   *
   * @private
   * @memberof Connection
   */
  installEvents() {
    if (!this._emitter.listeners(ConnectionEvent.CONNECTION_STATE_CHANGED).includes(this.handleConnectionStateChange)) {
      this._emitter.on(
        ConnectionEvent.CONNECTION_STATE_CHANGED,
        this.handleConnectionStateChange,
        this
      );
    }
  }

  uninstallEvents() {
    this._emitter.off(
      ConnectionEvent.CONNECTION_STATE_CHANGED,
      this.handleConnectionStateChange,
      this
    );
  }

  emitConnectionStateChangedEvent(state: ConnectionState, videoTrack?: LocalVideoTrack | LocalScreenTrack | null) {
    const prevState = this._currentState;
    const result = super.emitConnectionStateChangedEvent(state);
    if (result && prevState !== state) {
      if (videoTrack) {
        videoTrack.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
      } else {
        this.localMainVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
        this.localAuxVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
        this._publishingLocalVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
      }
    }
    return result;
  }

  /**
   * Publish a local stream via this uplink connection
   *
   * @param {LocalStream} stream Local stream created by TRTC.createStream()
   * @memberof Connection
   */
  @addCallQueue()
  @createDecorator<UplinkConnection>(next => function (this: UplinkConnection, ...args) {
    return new Promise((resolve, reject) => {
      const onClosed = (reason: string) => {
        this._emitter.off(ConnectionEvent.CLOSED, onClosed);
        reject(new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: generateErrorMessage({
            key: ErrorName.CONNECTION_ABORTED,
            data: reason
          })
        }));
      };
      this._emitter.on(ConnectionEvent.CLOSED, onClosed);
      next.apply(this, args).then(resolve, reject)
        .finally(() => {
          this._emitter.off(ConnectionEvent.CLOSED, onClosed);
        });
    });
  })
  async publish({ localAudioTrack, localVideoTrack, isAuxiliary }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null, isAuxiliary: boolean; }) {
    if (!this._peerConnection) {
      this.initialize();
    }
    if (localAudioTrack) {
      this._publishingLocalAudioTrack = localAudioTrack;
    }
    if (localVideoTrack) {
      this._publishingLocalVideoTrack = localVideoTrack;
    }
    this._isPublishingAux = isAuxiliary;

    let smallTrack = undefined;
    // 初始化小流
    if (localVideoTrack && !isAuxiliary && localVideoTrack.small) {
      smallTrack = this._room.videoManager.smallTrack;
    }

    this.sendMediaSettings();

    // 支持 getDisplayMedia 的浏览器，都支持 transceiver
    if (isAddTransceiverSupported()) {
      await this.publishByTransceiver({ localAudioTrack, localVideoTrack, smallTrack, isAuxiliary });
    } else {
      // 不支持 addTransceiver 的浏览器，也不支持 getDisplayMedia
      // 无法采集屏幕分享，也就无需推辅流，因此 fallback 到旧逻辑推流，只支持推主流。
      await this.publishByAddTrack({ localAudioTrack, localVideoTrack, smallTrack });
    }

    this._publishingLocalAudioTrack = null;
    this._publishingLocalVideoTrack = null;
    this._isPublishingAux = false;
    if (isAuxiliary) {
      if (localVideoTrack) {
        this.localAuxVideoTrack = localVideoTrack;
      }
      if (localAudioTrack) {
        this.localAuxAudioTrack = localAudioTrack;
      }
    } else {
      if (localVideoTrack) {
        this.localMainVideoTrack = localVideoTrack;
      }
      if (localAudioTrack) {
        this.localMainAudioTrack = localAudioTrack;
      }
    }
    this.installTrackMuteEvents(localAudioTrack, localVideoTrack);
    this.sendMutedFlag();
  }

  // Chrome 69+, Safari 11+, Firefox 59+
  async publishByTransceiver({ localAudioTrack, localVideoTrack, smallTrack, isAuxiliary }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null, smallTrack?: MediaStreamTrack, isAuxiliary: boolean; }) {
    this._log.info('publish by transceiver');

    const mediaStream = new MediaStream();

    const videoTrack = localVideoTrack?.outMediaTrack;
    const audioTrack = localAudioTrack?.outMediaTrack;

    if (audioTrack) {
      mediaStream.addTrack(audioTrack);
    }
    if (videoTrack) {
      mediaStream.addTrack(videoTrack);
    }

    const transceivers = this._peerConnection!.getTransceivers();
    if (transceivers.length === 0) {
      // add audio transceiver
      this._peerConnection!.addTransceiver(audioTrack || NAME.AUDIO, {
        direction: TRANSPORT_DIRECTION.SENDONLY,
        streams: [mediaStream]
      });
      // add video transceiver
      // 推屏幕分享流时，主流视频的 transceiver 位置传 'video'
      this._peerConnection!.addTransceiver(
        isAuxiliary ? NAME.VIDEO : videoTrack || NAME.VIDEO,
        {
          direction: TRANSPORT_DIRECTION.SENDONLY,
          streams: [mediaStream]
        }
      );
      // add small video transceiver
      this._peerConnection!.addTransceiver(smallTrack || NAME.VIDEO, {
        direction: TRANSPORT_DIRECTION.SENDONLY,
        streams: [mediaStream]
      });
      // add screen video transceiver
      this._peerConnection!.addTransceiver(
        isAuxiliary ? videoTrack || NAME.VIDEO : NAME.VIDEO,
        {
          direction: TRANSPORT_DIRECTION.SENDONLY,
          streams: [mediaStream]
        }
      );
      await this.connect();
    } else {
      const transceiverIndexList = [];
      if (audioTrack) {
        if (!transceivers[0].sender.track) {
          transceiverIndexList.push(0);
        }
        await transceivers[0].sender.replaceTrack(audioTrack);
        await this.setBandwidth({ bandwidth: localAudioTrack?.profile.bitrate || 40, type: NAME.AUDIO });
      }
      // 这两种情况会执行此处代码：
      // 1. 在已推主流的情况下、推辅流
      // 2. 在已推辅流的情况下、推主流
      if (videoTrack) {
        const videoTransceiverIndex = isAuxiliary ? 3 : 1;
        await transceivers[videoTransceiverIndex].sender.replaceTrack(videoTrack);
        await this.setBandwidth({
          bandwidth: localVideoTrack.profile.bitrate,
          type: NAME.VIDEO,
          videoType: isAuxiliary ? NAME.AUXILIARY : NAME.BIG
        });
        transceiverIndexList.push(videoTransceiverIndex);
        if (smallTrack) {
          await transceivers[2].sender.replaceTrack(smallTrack);
          await this.setBandwidth({
            bandwidth: localVideoTrack.small!.bitrate,
            type: NAME.VIDEO,
            videoType: NAME.SMALL
          });
          transceiverIndexList.push(2);
        }
      }
      await this.setTransceiverDirection(TRANSPORT_DIRECTION.SENDONLY, transceiverIndexList);
      await this.doPublishChange();
      localVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState: ConnectionState.DISCONNECTED, state: ConnectionState.CONNECTING });
      localVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState: ConnectionState.CONNECTING, state: ConnectionState.CONNECTED });
    }
  }

  // Chrome 56+, Safari 11+, Firefox 56+
  async publishByAddTrack({ localAudioTrack, localVideoTrack, smallTrack }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null, smallTrack?: MediaStreamTrack; }) {
    this._log.info('publish by addtrack');
    const videoTrack = localVideoTrack?.outMediaTrack;
    const audioTrack = localAudioTrack?.outMediaTrack;
    if (this._peerConnection && this._peerConnection.connectionState !== 'new') {
      if (localAudioTrack && audioTrack) {
        await this.addTrack(localAudioTrack);
      }
      if (videoTrack) {
        await this.addTrack(localVideoTrack);
      }
      return;
    }

    const mediaStream = new MediaStream();

    if (audioTrack) {
      mediaStream.addTrack(audioTrack);
    }
    if (videoTrack) {
      mediaStream.addTrack(videoTrack);
    }
    if (audioTrack) {
      this._peerConnection!.addTrack(audioTrack, mediaStream);
    }
    if (videoTrack) {
      this._peerConnection!.addTrack(videoTrack, mediaStream);
      if (smallTrack) {
        // 兼容 Chrome 64 以下版本，adapter.js polyfill 报错问题。
        const smallMediaStream = new MediaStream();
        smallMediaStream.addTrack(smallTrack);
        this._peerConnection!.addTrack(smallTrack, smallMediaStream);
      }
    }
    await this.connect();
  }

  async enableSmall(bool: boolean) {
    const transceivers = this._peerConnection!.getTransceivers();
    if (bool) {
      if (this._room.videoManager.smallTrack) {
        await transceivers[2].sender.replaceTrack(this._room.videoManager.smallTrack);
        await this.setTransceiverDirection(TRANSPORT_DIRECTION.SENDONLY, [2]);
      }
    } else {
      await transceivers[2].sender.replaceTrack(null);
      await this.setTransceiverDirection(TRANSPORT_DIRECTION.INACTIVE, [2]);
    }
    this.updateMediaSettings();
    await this.doPublishChange();
  }

  installTrackMuteEvents(...localTracks: (ILocalTrack | undefined | null)[]) {
    localTracks.forEach(localTrack => {
      if (localTrack) {
        localTrack?.on(TrackEvent.MUTE, this.sendMutedFlag, this);
        localTrack?.on(TrackEvent.UNMUTE, this.sendMutedFlag, this);
      }
    });
  }

  uninstallTrackMuteEvents(...localTracks: (ILocalTrack | undefined | null)[]) {
    localTracks.forEach(localTrack => {
      if (localTrack) {
        localTrack?.off(TrackEvent.MUTE, this.sendMutedFlag, this);
        localTrack?.off(TrackEvent.UNMUTE, this.sendMutedFlag, this);
      }
    });
  }

  @addCallQueue()
  @reportSuccessRate(KV_REPORT_KEY_NETWORK.UNPUBLISH, false)
  async unpublish({ localAudioTrack, localVideoTrack }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null; }) {
    if (!isGetTransceiversSupported()) {
      // 低版本 Chrome Cloud API 同时开启摄像头+麦克风的情况下，单独关闭摄像头 or 麦克风，此时走 removeTrack 逻辑
      if (localAudioTrack && localAudioTrack.outMediaTrack && !localVideoTrack && this.localMainVideoTrack) {
        await this.removeTrack(localAudioTrack);
        this.localMainAudioTrack = null;
        return;
      }
      if (localVideoTrack && localVideoTrack.outMediaTrack && !localAudioTrack && this.localMainAudioTrack) {
        await this.removeTrack(localVideoTrack);
        this.localMainVideoTrack = null;
        return;
      }
      await this.doUnpublish();
      this.uninstallTrackMuteEvents(localAudioTrack, localVideoTrack);
      this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED, localVideoTrack);
      return;
    }
    const isAuxiliary = localVideoTrack && localVideoTrack === this.localAuxVideoTrack;
    const videoTrack = localVideoTrack?.outMediaTrack;
    const senders = this._peerConnection!.getSenders();
    const transceiverIndexList = [];

    if (localAudioTrack) {
      if (isAuxiliary) {
        this.localAuxAudioTrack = null;
      } else {
        this.localMainAudioTrack = null;
      }
      if (!this.localAuxAudioTrack && !this.localMainAudioTrack) {
        await senders[0].replaceTrack(null);
        transceiverIndexList.push(0);
      }
    }

    if (videoTrack) {
      if (isAuxiliary) {
        await senders[3].replaceTrack(null);
        this.localAuxVideoTrack = null;
        this._mediaSettings = {
          ...this._mediaSettings,
          auxVideoBps: 0,
          auxVideoFps: 0,
          auxVideoWidth: 0,
          auxVideoHeight: 0
        };
        transceiverIndexList.push(3);
      } else {
        await senders[1].replaceTrack(null);
        await senders[2].replaceTrack(null);
        this.localMainVideoTrack = null;
        this._mediaSettings = {
          ...this._mediaSettings,
          videoWidth: 0,
          videoHeight: 0,
          videoBps: 0, // bps
          videoFps: 0,
          audioFs: 0, // 采样率
          audioChannel: 0, // 声道数
          audioBps: 0, // 单位bps

          smallVideoWidth: 0,
          smallVideoHeight: 0,
          smallVideoFps: 0,
          smallVideoBps: 0
        };
        transceiverIndexList.push(1, 2);
      }
    }
    if (this.isMainStreamPublished || this.isAuxStreamPublished) {
      await this.setTransceiverDirection(TRANSPORT_DIRECTION.INACTIVE, transceiverIndexList);
      await this.doPublishChange(false);
    } else {
      await this.doUnpublish();
    }
    this.uninstallTrackMuteEvents(localAudioTrack, localVideoTrack);
    localVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState: this._currentState, state: ConnectionState.DISCONNECTED });
  }

  async doPublishChange(enableLogWhenTimeout = true) {
    const data = {
      state: this._room.publishState,
      constraintConfig: this._mediaSettings
    };
    const result = await this._signalChannel.sendWaitForResponse({
      command: SignalCommand.PUBLISH_STATE_CHANGE,
      data,
      responseCommand: SignalEvent.PUBLISH_STATE_CHANGE_RESULT,
      enableLog: enableLogWhenTimeout
    });
    this.checkPublishResultCode(result.data.code, result.data.message);
  }

  doUnpublish(enableLogWhenTimeout = false): Promise<void | { data: SignalResponse; }> {
    return this._signalChannel
      .sendWaitForResponse({
        command: SignalCommand.UNPUBLISH,
        commandDesc: 'unpublish',
        responseCommand: SignalEvent.UNPUBLISH_RESULT,
        enableLog: enableLogWhenTimeout
      }).catch((error: RtcError) => {
        if (error.getCode() === ErrorCode.API_CALL_TIMEOUT) {
          return Promise.resolve();
        }
        throw error;
      });
  }

  updateMediaSettings() {
    const {
      detail: { isH264EncodeSupported, isVp8EncodeSupported }
    } = this._room.checkSystemResult;

    if (isH264EncodeSupported) {
      this._mediaSettings.videoCodec = 'H264';
    } else if (isVp8EncodeSupported) {
      this._mediaSettings.videoCodec = 'VP8';
    }

    const localAudioTrack = this._publishingLocalAudioTrack || this.localMainAudioTrack || this.localAuxAudioTrack;
    let { localMainVideoTrack, localAuxVideoTrack } = this;
    if (this._publishingLocalVideoTrack) {
      if (this._isPublishingAux) {
        localAuxVideoTrack = this._publishingLocalVideoTrack;
      } else {
        localMainVideoTrack = this._publishingLocalVideoTrack;
      }
    }
    if (IS_GET_SETTINGS_SUPPORTED) {
      if (localAudioTrack && localAudioTrack.outMediaTrack) {
        const settings = localAudioTrack.outMediaTrack.getSettings();
        // @ts-ignore
        this._mediaSettings.audioChannel = settings.channelCount || 1;
        this._mediaSettings.audioBps = localAudioTrack.profile.bitrate * 1000; // bps
        this._mediaSettings.audioFs = settings.sampleRate || 0;
      }

      if (localMainVideoTrack && localMainVideoTrack.outMediaTrack) {
        const settings = localMainVideoTrack.outMediaTrack.getSettings();
        this._mediaSettings.videoWidth = settings.width || 0;
        this._mediaSettings.videoHeight = settings.height || 0;
        this._mediaSettings.videoFps = settings.frameRate || 0;
        this._mediaSettings.videoBps = localMainVideoTrack.profile.bitrate * 1000; // bps

        if (localMainVideoTrack.small) {
          this._mediaSettings.smallVideoWidth = localMainVideoTrack.small.width;
          this._mediaSettings.smallVideoHeight = localMainVideoTrack.small.height;
          this._mediaSettings.smallVideoFps = localMainVideoTrack.small.frameRate;
          this._mediaSettings.smallVideoBps = localMainVideoTrack.small.bitrate * 1000;
        }
      }
      if (localAuxVideoTrack && localAuxVideoTrack.outMediaTrack) {
        const settings = localAuxVideoTrack.outMediaTrack.getSettings();
        this._mediaSettings.auxVideoWidth = settings.width || 0;
        this._mediaSettings.auxVideoHeight = settings.height || 0;
        this._mediaSettings.auxVideoFps = settings.frameRate || 0;
        this._mediaSettings.auxVideoBps = localAuxVideoTrack.profile.bitrate * 1000; // bps
      }
    } else {
      if (localAudioTrack && localAudioTrack.outMediaTrack) {
        this._mediaSettings.audioChannel = localAudioTrack.profile.channelCount;
        this._mediaSettings.audioBps = localAudioTrack.profile.bitrate * 1000; // bps
        this._mediaSettings.audioFs = localAudioTrack.profile.sampleRate;
      }
      if (localMainVideoTrack && localMainVideoTrack.outMediaTrack) {
        this._mediaSettings.videoWidth = localMainVideoTrack.profile.width;
        this._mediaSettings.videoHeight = localMainVideoTrack.profile.height;
        this._mediaSettings.videoFps = localMainVideoTrack.profile.frameRate;
        this._mediaSettings.videoBps = localMainVideoTrack.profile.bitrate * 1000; // bps
      }
    }
    this._log.info(`updateMediaSettings: ${JSON.stringify(this._mediaSettings)}`);
  }

  // 调用时机：本地推流后，业务侧调用 localStream.setVideoProfile 更新码率时。
  sendMediaSettings() {
    this.updateMediaSettings();
    this._signalChannel
      .sendWaitForResponse({
        command: SignalCommand.UPDATE_CONSTRAINT_CONFIG,
        data: this._mediaSettings,
        responseCommand: SignalEvent.UPDATE_CONSTRAINT_CONFIG_RES
      })
      .then(res => {
        if (res.data.code !== 0) {
          this._log.warn(res.data.message);
        }
      })
      .catch(() => { });
  }

  /**
   * 向 peerConnection 添加 track
   *
   * Chrome 69+ 使用 transceiver 方案
   * Chrome 56 ~ 68 使用旧版 peerConnection.addTrack 方案，向下兼容。
   * @param {MediaStreamTrack} track
   * @param {LocalStream} stream
   */
  async addTrack(localTrack: ILocalTrack) {
    if (!this._peerConnection) {
      return;
    }

    const isAuxiliary = localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;

    this._log.info(`is adding ${localTrack.kind} track to current published local ${isAuxiliary ? NAME.AUXILIARY : NAME.MAIN
    } stream`);

    if (isAddTransceiverSupported()) {
      await this.addTrackByTransceiver(localTrack, isAuxiliary);
    } else {
      await this.addTrackBySender(localTrack);
    }
  }

  // Chrome 69+
  async addTrackByTransceiver(localTrack: ILocalTrack, isAuxiliary: boolean) {
    if (!localTrack.mediaTrack) return;
    const transceivers = this._peerConnection!.getTransceivers();

    if (localTrack.kind === NAME.AUDIO) {
      await transceivers[0].sender.replaceTrack(localTrack.outMediaTrack);
    } else {
      const transceiverIndex = isAuxiliary ? 3 : 1;
      await transceivers[transceiverIndex].sender.replaceTrack(localTrack.outMediaTrack);

      if (transceiverIndex === 1 && this.localMainVideoTrack?.small) {
        await transceivers[2].sender.replaceTrack(this._room.videoManager.smallTrack);
      }
      if (transceivers[transceiverIndex].direction === TRANSPORT_DIRECTION.INACTIVE) {
        await this.setTransceiverDirection(TRANSPORT_DIRECTION.SENDONLY, [transceiverIndex]);
      }
    }
    this.updateMediaSettings();
    await this.doPublishChange();
  }

  // Chrome 56 ~ 68
  async addTrackBySender(localTrack: ILocalTrack) {
    if (!localTrack.outMediaTrack) return;
    const track = localTrack.outMediaTrack;

    // 多次 addTrack 失败时，再 addTrack 需要重新协商，将 stopping 的 transceiver 删除。
    if (
      isGetTransceiversSupported() &&
      this._peerConnection!.getTransceivers()
        .findIndex(transceiver => transceiver.direction === 'stopped') >= 0
    ) {
      this._log.warn('transceiver is stopping, negotiate sdp first');
      await this.updateOffer(TRACK_ACTION.REMOVE, track);
    }

    const sender = this._peerConnection!.getSenders()
      .find(sender => sender.track && sender.track.kind === track.kind);
    // 若第一次 addTrack update offer 超时失败，接入侧再调用一次 addTrack
    // 此时 PeerConnection 会存在第一次 addTrack 添加的 sender，该 sender 是无效的
    // 因此需要在第二次 addTrack 时将其清理
    if (sender && sender.track) {
      this._log.warn('sender already exists, remove sender first');
      const senderTrack = sender.track;
      this.removeSender(sender);
      await this.updateOffer(TRACK_ACTION.REMOVE, senderTrack);
    }
    // The adapter.js addTrack polyfill expects a MediaStream parameter
    if (track) this._peerConnection!.addTrack(track, new MediaStream([track]));


    if (track.kind === NAME.VIDEO && localTrack instanceof LocalVideoTrack && localTrack.small) {
      // 兼容 Chrome 64 以下版本，adapter.js polyfill 报错问题。
      // FIXME: Chrome 63 在 remove add video track 后，后台没上小流位，需找后台排查。
      const smallMediaStream = new MediaStream();
      const { smallTrack } = this._room.videoManager;
      smallMediaStream.addTrack(smallTrack);
      this._peerConnection!.addTrack(smallTrack, smallMediaStream);
    }

    await this.updateOffer(TRACK_ACTION.ADD, track);
  }

  /**
   * 判断是否需要重置 Offer SDP 的顺序，保证 SDP 是以 audio、video 的顺序排列
   * 此举是为了规避：
   * 1. unified-plan 的用户进房只推 video。
   * 2. add audio track，此时 offer sdp 的顺序变成了 video、audio。
   * 3. remove video track，由于 offer 中 mid=0 的描述是删不掉的，导致后台
   * 判断 track 增删出错，回的 answer 与 offer 对应不上，最终导致报错。
   * TODO: 此处是个临时的规避方案，后续要使用更好的方案规避。
   */
  isNeedToResetOfferOrder() {
    if (
      this._sdpSemantics === SDP_SEMANTICS_PLAN_B ||
      !this._peerConnection ||
      !this._peerConnection.localDescription
    ) {
      return false;
    }

    const { sdp } = this._peerConnection.localDescription;
    const parsedSDP = SdpParser.parse(sdp);
    for (let i = 0; i < parsedSDP.media.length; i++) {
      if (Number(parsedSDP.media[i].mid) === 0 && parsedSDP.media[i].type === NAME.VIDEO) {
        return true;
      }
    }
    return false;
  }

  removeSender(sender: RTCRtpSender) {
    let transceiver = null;
    // 找到 sender 对应的 transceiver
    if (isGetTransceiversSupported()) {
      transceiver = this._peerConnection!.getTransceivers()
        .find(transceiver => transceiver.sender && transceiver.sender.track === sender.track);
    }

    this._peerConnection!.removeTrack(sender);

    if (transceiver && isFunction(transceiver.stop)) {
      this._log.info('stop transceiver');
      transceiver.stop();
    }
  }

  /**
   * 向 peerConnection 移除 track
   *
   * Chrome 69+ 使用 transceiver 方案
   * Chrome 56 ~ 68 使用旧版 peerConnection.addTrack 方案，向下兼容。
   */
  async removeTrack(localTrack: ILocalTrack) {
    if (!this._peerConnection) {
      return;
    }

    const isAuxiliary = localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;


    this._log.info(`is removing ${localTrack.kind} track from current published local ${isAuxiliary ? NAME.AUXILIARY : NAME.MAIN
    } stream`);

    if (isAddTransceiverSupported()) {
      await this.removeTrackByTransceiver(localTrack, isAuxiliary);
    } else {
      await this.removeTrackBySender(localTrack);
    }
  }

  async removeTrackByTransceiver(localTrack: ILocalTrack, isAuxiliary: boolean) {
    if (!localTrack.outMediaTrack) return;
    const transceivers = this._peerConnection!.getTransceivers();
    if (localTrack.kind === NAME.AUDIO) {
      await transceivers[0].sender.replaceTrack(null);
    } else {
      const transceiverIndex = isAuxiliary ? 3 : 1;
      await transceivers[transceiverIndex].sender.replaceTrack(null);
      if (transceiverIndex === 1 && (localTrack as LocalVideoTrack).small) {
        // this._smallGenerator.destroy();
        // this._smallGenerator = null;
        await transceivers[2].sender.replaceTrack(null);
      }
      await this.setTransceiverDirection(TRANSPORT_DIRECTION.INACTIVE, [transceiverIndex]);
    }

    this.updateMediaSettings();
    await this.doPublishChange();
  }

  /**
   * Firefox 在 sender.replaceTrack(null) 后，还会继续发最后一帧数据，
   * 导致在取消推流、removeTrack 后，远端用户还能收到数据。
   *
   * 此处针对 Firefox 做兼容：
   * 1. 在 replaceTrack(null) 后将 transceiver.direction 设为 'inactive'
   * 2. 在 replaceTrack(track) 后再将 transceiver.direction 设为 'sendonly'
   * @param {'inactive'|'sendonly'} direction
   * @param {Number[]} transceiverIndexList
   * @returns
   */
  async setTransceiverDirection(direction: RTCRtpTransceiverDirection, transceiverIndexList: number[]) {
    if (!IS_FIREFOX) return;
    let isOfferDirectionChanged = false;
    let isAnswerDirectionChanged = false;
    this._log.info(`setting transceiver ${transceiverIndexList.join(',')} direction to ${direction}`);
    const transceivers = this._peerConnection!.getTransceivers();
    transceiverIndexList.forEach(transceiverIndex => {
      if (transceivers[transceiverIndex].direction !== direction) {
        transceivers[transceiverIndex].direction = direction;
        isOfferDirectionChanged = true;
      }
    });
    if (isOfferDirectionChanged) {
      this._log.info('updating offer');
      const offer = await this._peerConnection!.createOffer();
      await this.setOffer(offer);
    }
    let index = -1;
    // 更新 answer 中的 direction
    const sdp = this._peerConnection!.remoteDescription!.sdp.split('\r\n').map(item => {
      if (item.match(new RegExp(`a=(${TRANSPORT_DIRECTION.INACTIVE}|${TRANSPORT_DIRECTION.RECVONLY}|${TRANSPORT_DIRECTION.SENDONLY})`))) {
        index++;
      }
      if (transceiverIndexList.includes(index)) {
        // 将 answer 中的 recvonly 改为 inactive
        if (direction === TRANSPORT_DIRECTION.INACTIVE && item.includes(`a=${TRANSPORT_DIRECTION.RECVONLY}`)) {
          isAnswerDirectionChanged = true;
          return `a=${direction}`;
        }
        // 将 answer 中的 inactive 改为 recvonly
        if (direction === TRANSPORT_DIRECTION.SENDONLY && item.includes(`a=${TRANSPORT_DIRECTION.INACTIVE}`)) {
          isAnswerDirectionChanged = true;
          // 对于 offer 来说，direction 是 sendonly
          // 对于 answer 来说，direction 是 recvonly
          return `a=${TRANSPORT_DIRECTION.RECVONLY}`;
        }
      }
      return item;
    })
      .join('\r\n');
    if (isAnswerDirectionChanged) {
      this._log.info('updating answer');
      await this.setAnswer({ type: 'answer', sdp });
    }
  }

  /**
   * 移除上行流的 track
   * 1. 在支持 transceiver.stop 接口的浏览中（Firefox, Safari），使用 unified-plan
   * 需要在 removeTrack 后调用该接口。防止多次 addTrack, removeTrack 操作，
   * 导致上行 offer 越来越长。 offer 不干净会导致多种不可预知的问题。
   * 2. 在不支持该接口的浏览器中（Chrome, Edge），使用 plan-b。
   * plan-b 下 addTrack, removeTrack 操作不会导致上行 offer 越来越长。
   * @private
   * @memberof UplinkConnection
   */
  async removeTrackBySender(localTrack: ILocalTrack) {
    if (!localTrack.outMediaTrack) return;
    if (localTrack.kind === NAME.VIDEO && this.isNeedToResetOfferOrder() && this.localMainAudioTrack) {
      this.reset();
      this.initialize();
      await this.publish({ localAudioTrack: this.localMainAudioTrack, isAuxiliary: false });
      return;
    }
    const sender = this._peerConnection!.getSenders()
      .find(sender => sender.track === localTrack.outMediaTrack);
    if (sender) {
      this.removeSender(sender);
      if (localTrack.kind === NAME.VIDEO && (localTrack as LocalVideoTrack).small) {
        // 将小流的 sender 也移除掉，由于此处只有主路大小流，因此可以将所有 video sender 移除。
        this._peerConnection!.getSenders().forEach(sender => {
          if (sender.track && sender.track.kind === NAME.VIDEO) {
            this.removeSender(sender);
          }
        });
      }
    }

    // 若第一次 removeTrack update offer 超时失败，接入侧再重新调用 removeTrack
    // 第二次调用时，是找不到对应的 sender 的，因为在第一次调用时移除了。
    // 由于第一次调用时 update offer 失败了，此时需要更新 offer。
    await this.updateOffer(TRACK_ACTION.REMOVE, localTrack.outMediaTrack);
  }

  async replaceTrack(localTrack: ILocalTrack) {
    const senders = this._peerConnection?.getSenders();
    if (!senders || senders.length === 0 || !localTrack.mediaTrack) {
      return false;
    }

    let mainAudioOrVideoSender;
    if (isAddTransceiverSupported()) {
      mainAudioOrVideoSender = localTrack.kind === NAME.AUDIO ? senders[0] : senders[1];
    } else {
      // addTrack 推流时，可能先推视频，再推音频，此时 sender[0] 是 video 而不是 audio。因此不能用 index 找 sender。
      mainAudioOrVideoSender = senders.find(sender => sender.track && sender.track.kind === localTrack.kind);
    }
    if (!mainAudioOrVideoSender) return false;

    const isAuxiliary = localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;
    this._log.info(`is replacing ${localTrack.kind} track on ${isAuxiliary ? NAME.AUXILIARY : NAME.MAIN} stream`);
    if (localTrack.kind === NAME.AUDIO) {
      await mainAudioOrVideoSender.replaceTrack(localTrack.outMediaTrack);
    } else if (localTrack.kind === NAME.VIDEO) {
      if (!isAuxiliary) {
        await mainAudioOrVideoSender.replaceTrack(localTrack.outMediaTrack);
      } else if (senders[3]) {
        await senders[3].replaceTrack(localTrack.outMediaTrack);
      }
    }
    return true;
  }

  /**
   * 更新上行 offer
   * 需要告知后台，是 addTrack 还是 removeTrack，以及 track 的相关信息
   * @private
   * @param {('add'|'remove')} action
   * @param {MediaStreamTrack} track
   * @memberof Connection
   */
  async updateOffer(action: string, track: MediaStreamTrack) {
    try {
      // @ts-ignore
      const offer = await this._peerConnection!.createOffer(offerOptions);
      // 手动修改 direction 为 sendrecv，避免 Firefox 报错
      // ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view?bug_id=1020396022083137197
      if (IS_FIREFOX && offer.sdp) {
        offer.sdp = this.setSDPDirection(offer.sdp, 'sendrecv');
      }
      await this.setOffer(offer);
      const mediaSettings = this.updateMediaSettings();
      const data = {
        action,
        trackId: track.id,
        kind: track.kind === NAME.VIDEO ? 'bigVideo' : track.kind,
        type: 'offer',
        sdp: this._peerConnection!.localDescription!.sdp,
        constraintConfig: mediaSettings,
        state: this._room.publishState
      };
      this._log.info('createOffer success, sending updated offer to remote server');
      this._log.debug(`updatedOffer: ${data.sdp}`);
      const result = await this._signalChannel.sendWaitForResponse({
        command: SignalCommand.PUBLISH_CHANGE,
        data,
        responseCommand: SignalEvent.UPDATE_OFFER_RESULT,
        timeout: UPDATE_OFFER_TIMEOUT,
        commandDesc: 'update offer'
      });
      const { code, message } = result.data;
      if (code !== 0) {
        this.checkPublishResultCode(code, message);
      }
      await this.acceptAnswer(result.data.data);
      // update offer 后更新 ssrc，此处 ssrc 用于 2s 质量上报中区分大小流 track。
      if (offer.sdp) {
        this.updateSSRC(offer.sdp);
      }
    } catch (error) {
      this._log.error(error);
      throw error;
    }
  }


  /**
   * 设置上行音视频码率，策略如下：
   * 1. 优先使用 set maxBitrate 方式控制码率；
   * 优点：各浏览器均支持，不需要修改 SDP
   * 缺点：需要考虑低版本兼容性
   * 2. 若 set maxBitrate 不支持或者失败了，则使用
   * 修改 SDP 方式控制码率，作为备选方案。
   *
   * ref: https://developer.mozilla.org/en-US/docs/Web/API/RTCRtpEncodingParameters/maxBitrate
   * @private
   * @param {Number} bandwidth 码率，单位：kbps
   * @param {String} type video audio
   * @param {String} videoType big small auxiliary
   * @param {String} sdp
   * @return {String} sdp
   * @memberof Connection
   */
  async setBandwidth({ bandwidth, type, videoType, sdp }: BandWidthOption) {
    // 不支持 set maxBitrate，则使用修改 SDP 方式控制码率
    if (!isSetParametersSupported()) {
      if (!sdp) return;
      return type === NAME.VIDEO
        ? this.updateVideoBandwidthRestriction(sdp, bandwidth, videoType)
        : this.updateAudioBandwidthRestriction(sdp, bandwidth);
    }

    // In Chrome, use RTCRtpSender.setParameters to change bandwidth without
    // (local) renegotiation. Note that this will be within the envelope of
    // the initial maximum bandwidth negotiated via SDP.
    let sender: RTCRtpSender | undefined = undefined;
    const senders = this._peerConnection!.getSenders();
    if (isAddTransceiverSupported()) {
      let senderIndex = 0;
      if (type === NAME.VIDEO) {
        if (videoType === NAME.SMALL) {
          senderIndex = 2;
        } else if (videoType === NAME.AUXILIARY) {
          senderIndex = 3;
        } else {
          senderIndex = 1;
        }
      }
      sender = senders[senderIndex];
    } else {
      sender = senders.find(sender => sender.track && sender.track.kind === type);
    }
    if (sender) {
      const parameters = sender.getParameters();
      if (!parameters.encodings || parameters.encodings.length === 0) {
        parameters.encodings = [{}];
      }
      parameters.encodings[0].maxBitrate = bandwidth * 1000;
      try {
        await sender.setParameters(parameters);
        this._log.info(`${videoType || ''}${type} bandwidth ${bandwidth} kbps`);
        return sdp;
      } catch (error) {
        this._log.info(`failed to set bandwidth by setting maxBitrate: ${error}`);
        // set maxBitrate 失败，使用修改 SDP 方式控制码率
        if (sdp) {
          return type === NAME.VIDEO
            ? this.updateVideoBandwidthRestriction(sdp, bandwidth, videoType)
            : this.updateAudioBandwidthRestriction(sdp, bandwidth);
        }
      }
    }

    return sdp;
  }

  /**
   * 通过修改 SDP 方式控制视频码率
   * ref:
   * 1. http://www.rtcbits.com/2016/11/controlling-bandwidth-usage-in-webrtc.html
   * 2. https://github.com/webrtc/samples/blob/gh-pages/src/content/peerconnection/bandwidth/js/main.js#L227
   * @private
   */
  updateVideoBandwidthRestriction(sdp: string, bandwidth: number, videoType?: string) {
    let modifier = 'AS';
    if (IS_FIREFOX) {
      modifier = 'TIAS';
      bandwidth = bandwidth * 1000;
    }
    let index = 0;
    let matchIndex = -1;
    if (videoType === NAME.SMALL) {
      index = 1;
    } else if (videoType === NAME.AUXILIARY) {
      index = 2;
    }
    // insert b= after c= line of m=video
    sdp = sdp.replace(/m=video (.*)\r\nc=IN (.*)\r\n/g, match => {
      matchIndex += 1;
      if (matchIndex === index) {
        return `${match}b=${modifier}:${bandwidth}\r\n`;
      }
      return match;
    });
    return sdp;
  }

  updateAudioBandwidthRestriction(sdp: string, bandwidth: number) {
    let modifier = 'AS';
    if (IS_FIREFOX) {
      modifier = 'TIAS';
      bandwidth = bandwidth * 1000;
    }
    // insert b= after c= line of m=audio
    sdp = sdp.replace(
      /m=audio (.*)\r\nc=IN (.*)\r\n/,
      `m=audio $1\r\nc=IN $2\r\nb=${modifier}:${bandwidth}\r\n`
    );
    return sdp;
  }

  removeBandwidthRestriction(sdp: string) {
    return sdp.replace(/b=AS:.*\r\n/, '')
      .replace(/b=TIAS:.*\r\n/, '');
  }

  // 解决手机 webrtc 竖屏推流导致的旁路画面旋转问题
  removeVideoOrientation(sdp: string): string {
    return sdp.replace(/urn:3gpp:video-orientation/, '');
  }

  @after(Connection.prototype.afterConnect)
  @before(Connection.prototype.beforeConnect)
  async connect() {
    try {
      // 1. 交换 sdp
      await this.exchangeSDP();
      // 2. 等待 PeerConnection connected
      await this.waitForPeerConnectionConnected();
    } catch (error) {
      this.closePeerConnection(true);
      this.uninstallEvents();
      throw error;
    }
  }

  async exchangeSDP() {
    try {
      await this.createOffer();
      this._log.info('createOffer success, sending offer to remote server');
      await this.doExchangeSDP();
    } catch (error) {
      throw error;
    }
  }

  async createOffer() {
    try {
      // @ts-ignore
      const offer = await this._peerConnection!.createOffer(offerOptions);
      // 测试代码，可以模拟不支持 264 编码的环境
      // offer.sdp = SdpParser.removeH264(offer.sdp);
      if (offer.sdp) {
        offer.sdp = SdpParser.enableDtx(offer.sdp, this._enableDtx);
      }
      await this.setOffer(offer);
      if (offer.sdp) {
        this.updateSSRC(offer.sdp);
      }
    } catch (error) {
      throw error;
    }
  }

  doExchangeSDP() {
    const data = {
      command: SignalCommand.PUBLISH,
      responseCommand: SignalEvent.PUBLISH_RESULT,
      data: {
        type: this._peerConnection!.localDescription!.type,
        sdp: this.removeVideoOrientation(this._peerConnection!.localDescription!.sdp),
        screen: this.localMainVideoTrack instanceof LocalScreenTrack || this.localAuxVideoTrack instanceof LocalScreenTrack,
        state: this._room.publishState,
        constraintConfig: this._mediaSettings
      },
      enableLog: false
    };
    this._log.debug(`sending sdp offer: ${data.data.sdp}`);
    return this._signalChannel.sendWaitForResponse(data)
      .then((result: any) => {
        const { code, message, data } = result.data;
        if (code === 0) {
          return this.acceptAnswer(data);
        }
        return this.checkPublishResultCode(code, message);
      });
  }

  /**
   * 修改 SDP direction
   * @private
   * @param {String} sdp sdp
   * @param {('sendrecv'|'sendonly'|'recvonly')} direction sendrecv sendonly recvonly
   * @param {String} [type=all] all audio video
   * @return {String} sdp
   * @memberof Connection
   */
  setSDPDirection(sdp: string, direction: RTCRtpTransceiverDirection, type = 'all') {
    const parsedSDP = SdpParser.parse(sdp);
    parsedSDP.media.forEach(item => {
      if (type === 'all' || item.type === type) {
        // @ts-ignore
        item.direction = direction;
      }
    });
    return SdpParser.write(parsedSDP);
  }

  /**
   *
   * @private
   * @param {Object} answer
   * @memberof Connection
   */
  async acceptAnswer(answer: RTCSessionDescription) {
    try {
      let sdp;
      const stream = this._publishingLocalAudioTrack || this._publishingLocalVideoTrack || this.isMainStreamPublished;
      if (stream) {
        const videoBitrate = this._publishingLocalVideoTrack?.profile.bitrate || this.localMainVideoTrack?.profile.bitrate;
        const audioBitrate = this._publishingLocalAudioTrack?.profile.bitrate || this.localMainAudioTrack?.profile.bitrate;
        if (videoBitrate) {
          const videoType = this._isPublishingAux ? NAME.AUXILIARY : NAME.BIG;
          sdp = await this.setBandwidth({ bandwidth: videoBitrate, type: NAME.VIDEO, sdp, videoType });
        }
        if (audioBitrate) {
          sdp = await this.setBandwidth({ bandwidth: audioBitrate, type: NAME.AUDIO, sdp });
        }
      }
      sdp = this.removeVideoOrientation(answer.sdp);

      if (this._publishingLocalVideoTrack?.small) {
        const { smallStreamConfig } = this._room;
        sdp = await this.setBandwidth({
          bandwidth: this._publishingLocalVideoTrack.small.bitrate || smallStreamConfig.bitrate,
          type: NAME.VIDEO,
          videoType: NAME.SMALL,
          sdp
        });
      }

      const desc = { type: answer.type, sdp };
      await this.setAnswer(desc);
      this._log.debug(`accepted answer: ${sdp}`);
    } catch (error) {
      this._log.error(`failed to accept remote answer ${error}`);
      throw error;
    }
  }

  /**
   * 发送 muteState，更新当前上行的 mute 状态
   *
   * 调用时机：
   * 1. 接入侧调用 muteAudio/muteVideo/unmuteAudio/unmuteVideo 时
   * 2. 推流成功后需要同步本地的 muteFlag，
   *    2.1. 修复推流前 muteAudio & muteVideo 再推流，mute 状态不准的问题
   *    ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view/1020396022084583223
   *    2.2. 修复重新推流后，mute 状态不准的问题
   *    ref: http://tapd.oa.com/20396022/bugtrace/bugs/view?bug_id=1020396022084979351
   */
  sendMutedFlag(localTrack?: ILocalTrack) {
    // TODO: SDK 辅流暂不支持 mute 音视频，后台支持，后续再评估是否要支持
    const isAuxiliary = localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;
    if (isAuxiliary) return;

    this._log.info(`send muted state: ${JSON.stringify(this._room.muteState)}`);
    this._signalChannel.send(SignalCommand.UPDATE_MUTE_STAT, this._room.muteState);
  }

  getIsReconnecting() {
    return this._isReconnecting;
  }

  /**
   * 上行重连
   * @private
   * @memberof UplinkConnection
   */
  async reconnect() {
    const result = super.beforeReconnect();
    if (result < 0) return;

    try {
      // 上行重连需先 unpublish，通知后台
      // 收到 unpublish result 后再发 offer 重建 peerConnection
      // 直接发 offer 重建 peerConnection
      await this._signalChannel.sendWaitForResponse({
        command: SignalCommand.UNPUBLISH,
        responseCommand: SignalEvent.UNPUBLISH_RESULT,
        enableLog: false
      });
      this.closePeerConnection();
      this.initialize();
      if (this.isMainStreamPublished) {
        await this.publish({ localAudioTrack: this.localMainAudioTrack, localVideoTrack: this.localMainVideoTrack, isAuxiliary: false });
      }
      if (this.isAuxStreamPublished) {
        await this.publish({ localAudioTrack: this.localAuxAudioTrack, localVideoTrack: this.localAuxVideoTrack, isAuxiliary: true });
      }
      // eventLogger.logSuccessEvent({
      //   userId: this._room.userId,
      //   eventType: KIBANA_EVENT.UPLINK_RECONNECTION
      // });

      this._log.warn('reconnect() uplink reconnect successfully');
      this.stopReconnection();
    } catch (error) {
      const timeout = getReconnectionTimeout(this._reconnectionCount);
      this._log.warn(`reconnect() timeout, try again after ${timeout / 1000}s`);
      // @ts-ignore
      this._reconnectionTimer = setTimeout(() => {
        this.clearReconnectionTimer();
        this.reconnect();
      }, timeout);
    }
  }

  handleConnectionStateChange(event: { prevState: ConnectionState, state: ConnectionState; }) {
    if (event.state === ConnectionState.CONNECTED) {
      if (this.localMainVideoTrack || (this._publishingLocalVideoTrack && !this._isPublishingAux)) {
        innerEmitter.emit(INNER_EVENT.SEND_FIRST_VIDEO_FRAME, { room: this._room });
      }
    }
  }

  updateSSRC(sdp: string) {
    try {
      const parsedSDP = SdpParser.parse(sdp);
      parsedSDP.media.forEach((item, index) => {
        if (item.type === NAME.AUDIO) {
          const audioSSRC = item.ssrcs && item.ssrcs[0];
          if (audioSSRC) {
            this.ssrc.audio = Number(audioSSRC.id);
          }
        } else {
          // plan-b 和 unified-plan 获取 ssrc 方法不同
          if (this._sdpSemantics === SDP_SEMANTICS_PLAN_B && item.ssrcGroups) {
            item.ssrcGroups.forEach((ssrcGroupItem, i) => {
              const ssrc = Number(ssrcGroupItem.ssrcs.split(' ')[0]);
              // plan-b 不支持 transceiver api，因此没有辅流。无需更新辅流 ssrc
              if (i === 0) {
                this.ssrc.video = ssrc;
              } else if (i === 1) {
                this.ssrc.small = ssrc;
              }
            });
            return;
          }

          // 如下是 unified-plan 获取 ssrc 的方法
          const ssrc = item.ssrcs && item.ssrcs[0];
          if (!ssrc) {
            return;
          }

          switch (index) {
            // sdp 中第一个 video 描述是大流视频
            case 1:
              this.ssrc.video = Number(ssrc.id);
              break;
            // sdp 中第二个 video 描述是小流视频
            case 2:
              this.ssrc.small = Number(ssrc.id);
              break;
            // sdp 中第三个 video 描述是辅流视频
            case 3:
              this.ssrc.auxiliary = Number(ssrc.id);
              break;
            default:
              break;
          }
        }
      });
    } catch (error) {
      // ignore
    }
  }

  getVideoTrackId(type: string = NAME.VIDEO) {
    // 优先从 sender 中获取 trackId
    // iOS 15.1 通过推 canvas track 来规避 crash 问题，
    // localStream.getVideoTrack() 并不是真实推出去的 canvas track
    if (this._peerConnection) {
      const senders = this._peerConnection.getSenders();

      if (type === NAME.AUXILIARY && senders[3] && senders[3].track) {
        return senders[3].track.id;
      }
      if (type === NAME.VIDEO && senders[1] && senders[1].track) {
        return senders[1].track.id;
      }
    }
    if (this.localMainVideoTrack && type === NAME.VIDEO) {
      const videoTrack = this.localMainVideoTrack.mediaTrack;
      if (videoTrack) {
        return videoTrack.id;
      }
    }
    if (this.localAuxVideoTrack && type === NAME.AUXILIARY) {
      const videoTrack = this.localAuxVideoTrack.mediaTrack;
      if (videoTrack) {
        return videoTrack.id;
      }
    }
    return '';
  }

  getSSRC() {
    return this.ssrc;
  }

  /**
   * publish、publish_change 信令响应 code 处理
   * @param {Number} code 信令响应 code
   * @param {String} message 信令响应 message
   * @returns
   */
  checkPublishResultCode(code: number, message: string) {
    if (code === 0) {
      return;
    }
    if (code === NOT_SUPPORTED_H264) {
      // 支持VP8上行但 sdkappid 不在白名单，后端会返回这个 code 状态码
      this._log.error(ErrorInfo.NOT_SUPPORTED_H264ENCODE);
      // 在client 需要捕获该异常，避免重复调用 Publish
      throw new RtcError({
        code: ErrorCode.NOT_SUPPORTED_H264,
        message: generateErrorMessage({ key: ErrorName.NOT_SUPPORTED_H264ENCODE })
      });
    } else {
      throw new RtcError({
        code: ErrorCode.UNKNOWN,
        message: generateErrorMessage({
          key: ErrorName.SIGNAL_RESPONSE_FAILED,
          data: { signalResponse: SignalEvent.PUBLISH_RESULT, code, message }
        })
      });
    }
  }

  // getLocalStream() {
  //   return this._localStream;
  // }
}

export default UplinkConnection;
